<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search + Add Button -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model.trim="inputAllowed" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search or add allowed domains / IPs..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" v-on:keyup.enter="onEnter" :disabled="isSubmitting" />
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
               <button type="button" class="fw-bold btn btn-info text-light btn-sm"
                  style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap;"
                  @click="sendData" :disabled="isSubmitting">
                  <span v-if="isSubmitting">
                     <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Adding...
                  </span>
                  <span v-else>
                     <i class="fa-solid fa-plus me-1"></i> Allow Domain / IP
                  </span>
               </button>
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                     </option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col">Domain / IP</th>
                     <th scope="col" class="text-center">Upload Allowed</th>
                     <th scope="col" class="text-center">HTTP Allowed</th>
                     <th scope="col" class="text-center">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0 && !isLoading">
                  <tr>
                     <td colspan="4">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(allowedWebsite, index) in allowedWebsites" :key="index">
                     <td>
                        <i class="fa-solid fa-globe me-2" style="color: #00A0C1;"></i>
                        {{ allowedWebsite.domain }}
                        <span v-if="allowedWebsite.is_http_only == 1" class="badge bg-warning text-dark ms-2" style="font-size: 10px; border-radius: 4px;">HTTP Only</span>
                     </td>
                     <td class="text-center">
                        <div class="form-check form-switch d-flex justify-content-center">
                           <input class="form-check-input" type="checkbox" role="switch"
                              :id="'upload-toggle-' + index" @click="updateAllowUpload(index)"
                              :checked="allowedWebsite.allow_upload"
                              style="cursor: pointer; width: 2.5em; height: 1.25em;" />
                        </div>
                     </td>
                     <td class="text-center">
                        <div class="form-check form-switch d-flex justify-content-center">
                           <input class="form-check-input" type="checkbox" role="switch"
                              :id="'http-toggle-' + index" @click="updateAllowHttp(index)"
                              :checked="allowedWebsite.allow_http"
                              :disabled="allowedWebsite.is_http_only == 1"
                              style="cursor: pointer; width: 2.5em; height: 1.25em;" />
                        </div>
                     </td>
                     <td class="text-center">
                        <button type="button" class="ms-action-btn ms-action-btn--delete"
                            @click="deleteAllowedSite(index)"
                           title="Remove from allow list">
                           <i class="fa fa-xmark"></i>
                        </button>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
      </div>
   </div>
</template>

<script>
import { debounce, IsValidIPV4, ExtractDomain, IsValidWildcard, IsValidCIDR, IsUniversalWildcard, extractErrorMessage, IsLocalhost } from '@/plugins/helper.js'
export default {

   components: {

   },

   data() {
      return {
         headers: [
            { text: "Domain", value: "domain" },
            { text: "Upload Allowed", value: "allow_upload" },
            { text: "HTTP Allowed", value: "allow_http" },
            { text: "Actions", value: "actions" },
         ],
         debounceFn: null,
         allowedWebsites: [],
         inputAllowed: "",
         isIPToggled: false,
         isModalOpen: false,
         isLoading: false,
         isSubmitting: false,
         paginationData: {},
         pagination: {
            pageSize: 50,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "date_created__desc" },
            { text: "Date: Oldest", value: "date_created__asc" },
            { text: "Domain: A-Z", value: "domain__asc" },
            { text: "Domain: Z-A", value: "domain__desc" },
         ],
         sortBy: "domain__asc",
         sorting: {
            field: "domain",
            order: "asc"
         },
      };

   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },

      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() {
         this.getAllowList()
      },

      rowsPerPage() {
         this.pagination.page = 1
         this.getAllowList()
      },

      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getAllowList()
      },
   },

   mounted() {
      this.init();
   },

   created() {
      this.debounceFn = debounce(() =>
         this.getAllowList()
         , 500)
   },

   methods: {

      init() {
         this.getAllowList()
      },

      onSearch() {
         this.debounceFn()
      },

      openConfirmationModal() {
         this.isModalOpen = true;
      },
      closeConfirmationModal() {
         this.isModalOpen = false;
      },
      deleteItem() {
         this.closeConfirmationModal();
      },

      previousPage() {
         this.pagination.page > 1 ? this.pagination.page -= 1 : ''
      },

      nextPage() {
         this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : ''
      },

      onEnter: function () {
         if (this.isSubmitting) return;
         this.sendData()
      },

      async sendData() {
         if (this.isSubmitting) return;
         try {
            if (this.inputAllowed == null || this.inputAllowed.trim() == "") {
               this.$toast.error("Please provide a valid domain or IP.")
               return
            }

            // Security guardrail: reject universal wildcard
            if (IsUniversalWildcard(this.inputAllowed)) {
               this.$toast.error("Universal wildcard '*' is not permitted.")
               return
            }

            // Reject localhost
            if (IsLocalhost(this.inputAllowed)) {
               this.$toast.error("Localhost is permanently permitted. It cannot be added to allow list or block list.")
               return
            }

            let domLen = this.inputAllowed.length
            if (this.inputAllowed[domLen - 1] == '/' && !this.inputAllowed.includes('*')) {
               this.inputAllowed = this.inputAllowed.substring(0, domLen - 1)
            }

            let eDomain = ""

            let validIP = false;
            let validDomain = false;

            // Check for wildcard patterns (*.example.com, example.com/path/*)
            if (IsValidWildcard(this.inputAllowed)) {
               eDomain = this.inputAllowed
               validDomain = true
            // Check for CIDR notation (192.168.1.0/24)
            } else if (IsValidCIDR(this.inputAllowed)) {
               eDomain = this.inputAllowed
               validIP = true
            } else {
               validIP = IsValidIPV4(this.inputAllowed)

               if (!validIP) {
                  eDomain = ExtractDomain(this.inputAllowed)

                  if (eDomain) {
                     if (eDomain.includes("www.")) {
                        eDomain = eDomain.replace("www.", "")
                     }
                  }

                  validDomain = this.isValidDomain(eDomain)
               }
            }

            if (!validIP && !validDomain) {
               this.$toast.error("Please provide a valid domain or IP.")
               return
            }

            if (validIP) {
               eDomain = this.inputAllowed
            }

            this.isSubmitting = true

            const payload = {
               domain: eDomain.toLowerCase(),
               allow_upload: 1
            }

            const resp = await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/add-account-allow-site", payload)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
               this.inputAllowed = ""
               this.getAllowList()
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.inputAllowed = ""
               this.getAllowList()
            }
         } catch (error) {
            this.$toast.error(extractErrorMessage(error, "Failed to add website to allow list."))
            this.inputAllowed = ""
            this.getAllowList()
         } finally {
            this.isSubmitting = false
         }
      },

      async getAllowList() {

         let apiPath = "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-account-allow-list?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&sort_by=" + this.sorting.field
            apiPath += "&order_by=" + this.sorting.order
         }

         if (this.inputAllowed !== "" || this.inputAllowed !== null) {
            apiPath += "&search_key=" + this.inputAllowed
         }

         this.isLoading = true
         await this.$axios.get(apiPath).
            then((response) => {
               if (response.data.allow_list === "No Record Found")
                  this.allowedWebsites = []
               else {
                  this.allowedWebsites = response.data.allow_list
               }

               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               this.$toast.error(extractErrorMessage(error, "Failed to load allow list."))
            })
            .finally(() => {
               this.isLoading = false
            })

      },

      async deleteAllowedSite(index) {
         try {
            const payload = {
               domain: this.allowedWebsites[index].domain,
               id: this.allowedWebsites[index].id
            }

            const resp = await this.$axios.delete("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/remove-account-allow-site/" + this.allowedWebsites[index].id, payload)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.getAllowList()
            }

         } catch (error) {
            this.$toast.error(extractErrorMessage(error, "Failed to remove website from allow list."))
         }
      },

      async updateAllowUpload(index) {
         try {

            const payload = {
               id: this.allowedWebsites[index].id
            }

            const resp = await this.$axios.patch("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/update-allow-upload", payload)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.getAllowList()
            }

         } catch (error) {
            this.$toast.error(extractErrorMessage(error, "Failed to update upload permission."))
         }
      },

      async updateAllowHttp(index) {
         try {
            if (this.allowedWebsites[index].is_http_only == 1) {
               this.$toast.info("This is an HTTP-only website, so HTTP access must remain enabled.")
               return
            }

            const payload = {
               id: this.allowedWebsites[index].id
            }

            const resp = await this.$axios.patch("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/update-allow-http", payload)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.getAllowList()
            }

         } catch (error) {
            this.$toast.error(extractErrorMessage(error, "Failed to update HTTP permission."))
         }
      },

      isValidDomain(domain) {
         const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
         return domainPattern.test(domain);
      },

      extractDomain(url) {
         try {
            if (this.isValidDomain(url))
               return url

            let parsedURL = new URL(url);
            let domain = parsedURL.hostname;
            return domain;
         } catch (err) {
            if (err) {
               return ""
            }
         }
      }
   }

}

</script>

<style>
/* Shared Spark styles (.filterTitle, .filterOptions, .bordered-select,
   .pagination, .pagination-label, .pagination-arrow, .mainTable)
   have been moved to app.css for global availability. */
</style>